import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import { StyledSnackbar } from "../common/AppSnackbar";
import { useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import ErrorIcon from "@mui/icons-material/Error";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Dropzone from "react-dropzone";
import DropZone from "../common/DropZone";
import { send_request } from "../../utils/Request";
import { format } from "../../utils/Date";
import useProjectStore from "../ProjectCanvas/useProjectStore";
import CONFIG from "../../config";
import FilesTable from "../pages/FilesPage/FilesTable";
import CustomPaper from "../common/CustomPaper";

const fileService = CONFIG.FILE_SERVICE;

export default function FileLibrary({ params: externalParams }) {
  // Support both route usage (useParams) and modal usage (params prop)
  const routeParams = useParams();
  const projectId = externalParams?.id || routeParams?.id;
  const isModal = !!externalParams;

  const [files, setFiles] = useState([]);
  const [bulkData, setBulkData] = useState([]);
  const [projectName, setProjectName] = useState("Untitled");
  const [filesLoading, setFilesLoading] = useState(true);
  const [totalFileSize, setTotalFileSize] = useState(0);
  const [totalFileCount, setTotalFileCount] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [sortModel, setSortModel] = useState([
    { field: "createDate", sort: "desc" },
  ]);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

  // Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [dropFileLoading, setDropFileLoading] = useState(false);
  const [storageCheck, setStorageCheck] = useState(false);
  const [fileSizeCheck, setFileSizeCheck] = useState(false);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Zip state
  const [zipLoading, setZipLoading] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = useCallback((message, severity) => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const getData = useCallback(async () => {
    if (!projectId) return;
    setRefreshLoading(true);
    try {
      const sort = sortModel[0] || { field: "createDate", sort: "desc" };
      const params = new URLSearchParams({
        page: String(paginationModel.page),
        size: String(paginationModel.pageSize),
        search: debouncedSearchValue,
        sortField: sort.field || "createDate",
        sortDirection: sort.sort || "desc",
      });
      const json = await send_request(
        `project-service/project/file-library/${projectId}?${params.toString()}`,
        "",
        {},
        "get"
      );
      if (json?.data) {
        const {
          files: pageFiles,
          zipFiles,
          name,
          totalSize,
          totalCount,
        } = json.data;
        const fileData = pageFiles || [];
        const bulk = zipFiles || [];

        // Remove sentinel entries from bulk data
        const sentinelIndex = bulk.findIndex((item) => item.id === 1);
        const cleanBulk =
          sentinelIndex !== -1 ? bulk.slice(0, sentinelIndex) : bulk;

        setFiles(fileData);
        setBulkData(cleanBulk);
        setProjectName(name || "Untitled");
        setTotalFileSize(totalSize || 0);
        setTotalFileCount(totalCount || 0);
        setDeleteLoading(false);
      }
    } catch (err) {
      console.error("Error fetching file library:", err);
    } finally {
      setRefreshLoading(false);
      setFilesLoading(false);
    }
  }, [projectId, paginationModel, debouncedSearchValue, sortModel]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  const handleDeleteSelected = useCallback(async () => {
    setDeleteLoading(true);
    try {
      const json = await send_request(
        "project-service/project/file-library/deletes",
        { deleteList: selectedFiles, project_id: projectId },
        "",
        "POST"
      );
      if (json?.data) {
        setSelectedFiles([]);
        setDeleteDialogOpen(false);
        getData();
      }
    } catch (err) {
      console.error("Error deleting files:", err);
      setDeleteLoading(false);
    }
  }, [selectedFiles, projectId, getData]);

  const handleZipMultipleFiles = useCallback(async () => {
    let totalZipSize = 0;
    selectedFiles.forEach((id) => {
      const file = files.find((f) => f.id === id);
      if (file) totalZipSize += file.fileSize || 0;
    });

    if (totalZipSize > 10000000) {
      showSnackbar("Total zip size cannot exceed 10MB", "error");
      return;
    }

    setZipLoading(true);
    setBulkData((prev) => [
      ...prev,
      { id: 1, configId: "zipfiles", status: "GENERATING" },
    ]);

    try {
      const json = await send_request(
        `project-service/project/file-library/zip-multiple-files/${projectId}`,
        { listFileId: selectedFiles, projectName },
        "",
        "POST",
        { Accept: "application/json", "Content-Type": "application/json" }
      );

      if (json?.data) {
        setZipLoading(false);
        await getData();
        showSnackbar("Files have been zipped for download", "success");
      } else {
        setZipLoading(false);
        setBulkData((prev) => prev.filter((item) => item.id !== 1));
        showSnackbar("Zip files failed", "error");
      }
    } catch (err) {
      console.error("Error zipping files:", err);
      setZipLoading(false);
      setBulkData((prev) => prev.filter((item) => item.id !== 1));
      showSnackbar("Zip files failed", "error");
    }
  }, [selectedFiles, files, projectId, projectName, getData, showSnackbar]);

  const handleDownloadBulk = useCallback((id) => {
    setBulkData((prev) => prev.filter((item) => item.id !== id));
    window.location.href = `${fileService.QUERY_URL}/${id}`;
  }, []);

  const timeLeft = useCallback((createDate) => {
    if (!createDate) return "calculating...";
    const createTime = format(
      createDate,
      useProjectStore.getState().timezone,
      "relative"
    );
    const timeComponent = createTime.split(" ");
    let timeAvailable = 30;
    if (parseInt(timeComponent[0]) >= 1) {
      timeAvailable -= parseInt(timeComponent[0]);
    }
    if (timeAvailable <= 0) timeAvailable = 0;
    return `${timeAvailable} minute${timeAvailable === 1 ? "" : "s"}`;
  }, []);

  const getOrganizationInfo = useCallback(async () => {
    try {
      const json = await send_request(
        "project-service/organization/getRemainingFileStorage",
        "",
        {}
      );
      return json?.data || 0;
    } catch {
      return 0;
    }
  }, []);

  const handleFileDrop = useCallback(
    async (droppedFiles) => {
      if (!droppedFiles?.length) return;
      setDropFileLoading(true);
      const fileStorage = await getOrganizationInfo();
      const droppedFile = droppedFiles[0];

      if (fileStorage >= droppedFile.size) {
        if (droppedFile.size < 10000000) {
          setUploadFile(droppedFile);
          setStorageCheck(false);
          setFileSizeCheck(false);
        } else {
          setFileSizeCheck(true);
          setStorageCheck(false);
        }
      } else {
        setStorageCheck(true);
        setFileSizeCheck(false);
      }
      setDropFileLoading(false);
    },
    [getOrganizationInfo]
  );

  const handleUploadFile = useCallback(async () => {
    if (!projectId || !uploadFile) return;
    setUploadLoading(true);
    setStorageCheck(false);
    setFileSizeCheck(false);

    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("configId", "fileupload");
    formData.append("type", "upload");
    formData.append("projectId", projectId);
    formData.append("projectSessionId", "");
    formData.append("formSessionId", "");

    try {
      const json = await send_request(
        "project-service/project/file-library/create",
        formData,
        "",
        "POST",
        { "content-type": "multipart/form-data" }
      );
      if (json?.data) {
        setUploadFile(null);
        setUploadDialogOpen(false);
        await getData();
      }
    } catch (err) {
      console.error("Error uploading file:", err);
    } finally {
      setUploadLoading(false);
    }
  }, [projectId, uploadFile, getData]);

  const handleSelectFile = useCallback((fileId) => {
    setSelectedFiles((prev) =>
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedFiles((prev) => (prev.length > 0 ? [] : files.map((f) => f.id)));
  }, [files]);

  const handleSearchChange = useCallback((nextValue) => {
    setSearchValue(nextValue);
    setSelectedFiles([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleSortModelChange = useCallback((nextModel) => {
    setSortModel(nextModel);
    setSelectedFiles([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handlePaginationModelChange = useCallback((nextModel) => {
    setSelectedFiles([]);
    setPaginationModel(nextModel);
  }, []);

  const goBack = useCallback(() => {
    window.location.href = `/project/canvas/${projectId}`;
  }, [projectId]);

  if (filesLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton
          variant="rectangular"
          height={57}
          sx={{ mb: 2, borderRadius: 1 }}
        />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Page Header */}
      {!isModal && (
        <CustomPaper
          sx={{
            height: 57,
            minHeight: 57,
            display: "flex",
            alignItems: "center",
            px: 2,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            borderRadius: 0,
            p: 0,
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <IconButton size="small" onClick={goBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            onClick={goBack}
            sx={{
              fontSize: "1.1rem",
              fontWeight: 500,
              maxWidth: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {projectName !== "Untitled" ? projectName : projectId}
          </Typography>
        </CustomPaper>
      )}

      {/* Bulk ZIP download banners */}
      {bulkData.length > 0 && (
        <Box sx={{ px: 2, pt: 1 }}>
          {bulkData.map((item) => (
            <Alert
              key={item.id}
              severity={item.status === "GENERATING" ? "info" : "warning"}
              icon={<ErrorIcon />}
              sx={{
                mb: 1,
                cursor: item.status !== "GENERATING" ? "pointer" : "default",
              }}
              onClick={
                item.status !== "GENERATING"
                  ? () => handleDownloadBulk(item.id)
                  : undefined
              }
            >
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    textDecoration:
                      item.status !== "GENERATING" ? "underline" : "none",
                    cursor:
                      item.status !== "GENERATING" ? "pointer" : "default",
                    fontWeight: 500,
                  }}
                >
                  {item.fileName || "Generating zip file..."}
                </Typography>
                {item.status !== "GENERATING" && (
                  <>
                    <Typography variant="body2">
                      generated{" "}
                      {format(
                        item.createDate,
                        useProjectStore.getState().timezone,
                        "relative"
                      )}
                      .
                    </Typography>
                    <Typography variant="body2">
                      This link will expire in {timeLeft(item.createDate)}.
                    </Typography>
                  </>
                )}
              </Box>
            </Alert>
          ))}
        </Box>
      )}

      {/* File Table */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <FilesTable
          files={files}
          filesLoading={false}
          totalFileSize={totalFileSize}
          headerTitle={projectName}
          emptyMessage="No files in this workflow's library"
          selectable
          selectedFiles={selectedFiles}
          onSelectFile={handleSelectFile}
          onSelectAll={handleSelectAll}
          onSnackbar={showSnackbar}
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          paginationMode="server"
          rowCount={totalFileCount}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          displayedFileCount={totalFileCount}
          headerActions={
            <>
              {selectedFiles.length > 0 && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  Delete Selected
                </Button>
              )}
              {selectedFiles.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleZipMultipleFiles}
                  disabled={zipLoading}
                >
                  {zipLoading ? "Zipping..." : "Download Selected (zip)"}
                  {zipLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
                </Button>
              )}
              <IconButton
                size="small"
                onClick={getData}
                disabled={refreshLoading}
              >
                <RefreshIcon fontSize="small" />
                {refreshLoading && (
                  <CircularProgress size={16} sx={{ position: "absolute" }} />
                )}
              </IconButton>
              <Button
                variant="contained"
                size="small"
                startIcon={<UploadFileIcon />}
                onClick={() => setUploadDialogOpen(true)}
              >
                Upload File
              </Button>
            </>
          }
        />
      </Box>

      {/* Upload Dialog */}
      <Dialog
        fullWidth
        maxWidth="sm"
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
      >
        <DialogTitle>Upload file</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Drop a file into the area below or click and select a file to upload
            it
          </Typography>
          <Box sx={{ mb: 2 }}>
            {dropFileLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 3 }}>
                <CircularProgress size={24} />
                <Typography variant="body2">Uploading file...</Typography>
              </Box>
            ) : (
              <>
                <Dropzone multiple={false} onDrop={handleFileDrop}>
                  {({ getRootProps, getInputProps }) => (
                    <Box {...getRootProps()}>
                      <DropZone />
                      <input type="file" {...getInputProps()} />
                    </Box>
                  )}
                </Dropzone>
                {uploadFile?.name && (
                  <Box sx={{ pt: 1 }}>
                    <Chip
                      label={uploadFile.name}
                      onDelete={() => setUploadFile(null)}
                      color="primary"
                      sx={{ bgcolor: "rgba(33, 150, 243, 0.6)" }}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
          {storageCheck && (
            <Alert severity="error" sx={{ mb: 1 }}>
              You do not have enough file storage to upload this file. Either
              delete files from your file library to free up space or{" "}
              <Typography
                component="span"
                sx={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={() => {
                  window.location.href = "/organization";
                }}
              >
                upgrade your plan
              </Typography>{" "}
              to increase your file storage limit.
            </Alert>
          )}
          {fileSizeCheck && (
            <Alert severity="error" sx={{ mb: 1 }}>
              File exceeds maximum size of 10 MB
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setUploadDialogOpen(false);
              setFileSizeCheck(false);
            }}
            disabled={dropFileLoading || uploadLoading}
            variant="outlined"
            color="info"
          >
            Back
          </Button>
          <Button
            onClick={handleUploadFile}
            disabled={
              dropFileLoading ||
              uploadLoading ||
              storageCheck ||
              fileSizeCheck ||
              !uploadFile
            }
            variant="contained"
          >
            Upload
            {uploadLoading && <CircularProgress size={20} sx={{ ml: 1 }} />}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        fullWidth
        maxWidth="sm"
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete selected files</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Are you sure you want to delete these files? This cannot be undone
            and any links or references to these files will be also broken.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="contained"
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSelected}
            variant="outlined"
            color="error"
            disabled={deleteLoading}
          >
            Delete
            {deleteLoading && <CircularProgress size={20} sx={{ ml: 1 }} />}
          </Button>
        </DialogActions>
      </Dialog>

      <StyledSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
}
